import React from 'react';
import avatarCardImg from '@/assets/images/avatar-card.webp';
import { InfoCircle, UserAdd } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Patient } from '@/types/patient';
import { resolvePatientAge } from '@/utils/resolvePatientAge';

type AvailablePlan = React.ComponentProps<typeof FeatureUnavailableChip>['availablePlan'];

const patientDisplayName = (patient: Patient) => {
    const patientAge = resolvePatientAge({
        dateOfBirth: patient.date_of_birth,
        fallbackAge: patient.age,
    });

    return patientAge != null ? `${patient.full_name} - ${patientAge} anos` : patient.full_name;
};

interface ChoosePatientPanelProps {
    patients: Patient[];
    /** False until the first fetch settles — see the note on the empty state below. */
    hasLoadedPatients: boolean;
    selectedPatientId: string;
    onSelectPatient: (value: string) => void;
    onAddNewPatient: () => void;
    addPatientBlockedPlan: AvailablePlan | null;
    createReportBlockedPlan: AvailablePlan | null;
    createReportBlockedMessage?: string;
}

/**
 * What the patient step shows while the report does not exist yet.
 *
 * There is exactly one decision to make here — the report is created the moment
 * a patient is confirmed — so the panel is one heading and one control, and
 * everything that depends on that report (the evaluation period, the age at
 * exam) is left out until it exists. What the choice actually costs is stated
 * twice over by its neighbours already: the confirmation modal that follows it
 * and the block the other sections show, so it rides the hint icon here instead
 * of a third paragraph.
 */
export const ChoosePatientPanel: React.FC<ChoosePatientPanelProps> = ({
    patients,
    hasLoadedPatients,
    selectedPatientId,
    onSelectPatient,
    onAddNewPatient,
    addPatientBlockedPlan,
    createReportBlockedPlan,
    createReportBlockedMessage,
}) => {
    // The list is `/patients/available`, so "empty" also means every patient
    // already has an open report — hence the wording below, and hence waiting
    // for the fetch to settle rather than trusting the `initialData` empty array.
    const hasNoAvailablePatients = hasLoadedPatients && patients.length === 0;

    return (
        <div className="space-y-6">
            <div className="flex flex-col items-center gap-4 text-center">
                <img
                    src={avatarCardImg}
                    alt=""
                    // Intrinsic size, so the panel reserves the box instead of
                    // reflowing when the art lands.
                    width={917}
                    height={833}
                    decoding="async"
                    className="h-28 w-auto object-contain sm:h-32"
                />
                <div className="flex items-center justify-center gap-1.5">
                    <h3 className="text-base font-semibold text-foreground">
                        Escolha o paciente para criar o laudo
                    </h3>
                    <TooltipProvider delayDuration={150}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    aria-label="Mais informações"
                                    className="flex size-4 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                                >
                                    <InfoCircle className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs whitespace-normal">
                                O laudo é criado quando você confirmar o paciente. Depois disso o
                                paciente não pode ser trocado, e as demais seções ficam disponíveis.
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>

            {hasNoAvailablePatients ? (
                <div className="space-y-2">
                    {/* With nothing to pick from, the dropdown has nothing to
                        offer — so the one way forward has to stand on its own
                        instead of hiding inside it. */}
                    <Button type="button" className="w-full" onClick={onAddNewPatient}>
                        <UserAdd className="size-4" />
                        Adicionar novo paciente
                        {addPatientBlockedPlan ? (
                            <FeatureUnavailableChip availablePlan={addPatientBlockedPlan} />
                        ) : null}
                    </Button>
                    <p className="text-center text-xs leading-5 text-muted-foreground">
                        Nenhum paciente disponível para um novo laudo.
                    </p>
                </div>
            ) : (
                <Select value={selectedPatientId} onValueChange={onSelectPatient}>
                    <SelectTrigger aria-label="Paciente">
                        <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="new_patient">
                            <div className="flex items-center justify-between gap-3 font-medium text-blue-600">
                                <span className="inline-flex items-center gap-2">
                                    <UserAdd className="size-4" />
                                    Adicionar novo paciente
                                </span>
                                {addPatientBlockedPlan ? (
                                    <FeatureUnavailableChip availablePlan={addPatientBlockedPlan} />
                                ) : null}
                            </div>
                        </SelectItem>
                        {hasLoadedPatients ? (
                            <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                                    Pacientes
                                </div>
                                {patients.map((patient: Patient) => (
                                    <SelectItem key={patient.id} value={patient.id}>
                                        {patientDisplayName(patient)}
                                    </SelectItem>
                                ))}
                            </>
                        ) : (
                            <SelectItem value="loading" disabled>
                                Carregando pacientes...
                            </SelectItem>
                        )}
                    </SelectContent>
                </Select>
            )}

            {createReportBlockedPlan ? (
                <div className="flex justify-center">
                    <FeatureUnavailableChip
                        availablePlan={createReportBlockedPlan}
                        message={createReportBlockedMessage}
                    />
                </div>
            ) : null}
        </div>
    );
};
